'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { notifyBroadcastConfigurationChanged } from './broadcast-cache';
import { fetchFieldProjectionSnapshot, fetchFieldRollout, fetchFieldStreamCursor, fetchWorkerMobileSnapshot, type FieldRolloutState, type ProductDetailSnapshot } from './client';
import { DeviceWarningClearStateProvider } from './device-warning-clear-state';
import { createNormalizedFieldState, normalizedFieldReducer, selectFieldDataProvider, type NormalizedFieldState, type VersionedRuntimeEntity } from './normalized-store';
import { workerMobileProjectionSchema, workerSafetyEventSchema } from '../worker-mobile/model';
import { isRetryableReadError, retryRead } from '@/shared/api/read-recovery';
import { mergeWidgetSnapshot, type WidgetRefreshSnapshot } from './widget-refresh';

export interface IoTFieldDataContextValue extends NormalizedFieldState {
  activePhase: string;
  refresh: () => void;
  applyProductSnapshot: (snapshot: ProductDetailSnapshot) => void;
  loadStatus: 'LOADING' | 'READY' | 'ERROR' | 'FORBIDDEN';
  connectionStatus: 'IDLE' | 'CONNECTING' | 'CONNECTED' | 'RECONNECTING' | 'FAILED';
  error: string | null;
  canWrite: boolean;
}

const defaultContextValue: IoTFieldDataContextValue = {
  ...createNormalizedFieldState(),
  activePhase: 'V2_ACTIVE',
  refresh: () => undefined,
  applyProductSnapshot: () => undefined,
  loadStatus: 'LOADING',
  connectionStatus: 'IDLE',
  error: null,
  canWrite: false,
};

const IoTFieldDataContext = createContext<IoTFieldDataContextValue>(defaultContextValue);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isRuntimeEntity(value: unknown): value is VersionedRuntimeEntity & { entity_type?: unknown } {
  if (!isRecord(value)) return false;
  return typeof value.runtime_key === 'string' && typeof value.field_uid === 'string' && typeof value.version === 'number' && Number.isSafeInteger(value.version);
}

/** 같은 현장의 보드와 전역 알람은 하나의 snapshot과 스트림 경계를 공유합니다. */
export function IoTFieldDataProvider({ fieldUid, children, isolateRefresh = false }: { fieldUid: string; children: React.ReactNode; isolateRefresh?: boolean }) {
  const parent = useContext(IoTFieldDataContext);
  const content = isolateRefresh ? <IoTWidgetDataProvider>{children}</IoTWidgetDataProvider> : children;
  if (parent !== defaultContextValue && parent.fieldUid === fieldUid) return <>{content}</>;
  return <IoTFieldDataScope fieldUid={fieldUid}>{content}</IoTFieldDataScope>;
}

/** 수동 재조회 상태와 응답만 위젯에 격리하고 현장의 기존 실시간 구독을 공유합니다. */
export function IoTWidgetDataProvider({ children }: { children: React.ReactNode }) {
  const parent = useContext(IoTFieldDataContext);
  const requestRef = useRef<{ controller: AbortController; loading: boolean } | null>(null);
  const [local, setLocal] = useState<{
    base: IoTFieldDataContextValue;
    status: IoTFieldDataContextValue['loadStatus'];
    error: string | null;
    data: WidgetRefreshSnapshot | null;
  } | null>(null);

  useEffect(() => {
    // 현장·권한·운영 세대와 공통 초기 조회가 바뀌면 이전 위젯 응답을 폐기합니다.
    return () => {
      requestRef.current?.controller.abort();
      requestRef.current = null;
    };
  }, [parent.fieldUid, parent.routingVersion, parent.ownershipEpoch, parent.loadStatus, parent.snapshotAlarmVersionsByRuntimeKey]);

  const refresh = useCallback(() => {
    if (requestRef.current?.loading) return;
    requestRef.current?.controller.abort();
    if (parent.loadStatus !== 'READY' || parent.accessRevoked) {
      // 공통 구독 자체가 실패한 경우에만 기존 현장 복구 절차를 사용합니다.
      parent.refresh();
      return;
    }
    const controller = new AbortController();
    const request = { controller, loading: true };
    requestRef.current = request;
    setLocal({ base: parent, status: 'LOADING', error: null, data: null });
    void (async () => {
      try {
        const rollout = await retryRead(() => fetchFieldRollout(parent.fieldUid, controller.signal), controller.signal);
        if (rollout.active_phase !== parent.activePhase || rollout.routing_version !== parent.routingVersion || rollout.ownership_epoch !== parent.ownershipEpoch) {
          // 운영 세대 변경은 위젯만으로 복구하지 않고 공통 권한·구독 경계를 재확인합니다.
          parent.refresh();
          throw new Error('현장 운영 정보가 변경되어 다시 조회하고 있습니다.');
        }
        // 모바일 지연·실패는 기존 제품·알람의 표시와 수동 재조회를 막지 않습니다.
        const mobileSnapshot = fetchWorkerMobileSnapshot(parent.fieldUid, controller.signal, rollout).catch(() => null);
        const snapshot = await retryRead(async () => {
          const attempt = new AbortController();
          try {
            return await fetchFieldProjectionSnapshot(parent.fieldUid, AbortSignal.any([controller.signal, attempt.signal]), rollout);
          } finally {
            // 병렬 조회 일부가 실패하면 남은 요청도 취소한 뒤 재시도합니다.
            attempt.abort();
          }
        }, controller.signal);
        if (controller.signal.aborted) return;
        const data: WidgetRefreshSnapshot = { base: parent, snapshot, mobileDevices: null };
        setLocal({ base: parent, status: 'READY', error: null, data });
        request.loading = false;
        const mobileDevices = await mobileSnapshot;
        if (!controller.signal.aborted && mobileDevices !== null) {
          // 새 클릭·현장 전환 뒤 완료된 이전 모바일 응답은 현재 위젯을 덮어쓰지 않습니다.
          setLocal(current => (current?.data === data ? { ...current, data: { ...data, mobileDevices } } : current));
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        const status = isRecord(error) ? error.status : null;
        // 인증·권한 오류는 다른 위젯도 공통 경계에서 다시 확인하고 쓰기를 차단합니다.
        if (status === 401 || status === 403) parent.refresh();
        setLocal({
          base: parent,
          status: status === 403 ? 'FORBIDDEN' : 'ERROR',
          error: error instanceof Error ? error.message : '위젯 데이터를 불러오지 못했습니다.',
          data: null,
        });
      } finally {
        controller.abort();
        if (requestRef.current === request) requestRef.current = null;
      }
    })();
  }, [parent]);

  const value = useMemo<IoTFieldDataContextValue>(() => {
    const current =
      local &&
      parent.loadStatus === 'READY' &&
      !parent.accessRevoked &&
      local.base.fieldUid === parent.fieldUid &&
      local.base.routingVersion === parent.routingVersion &&
      local.base.ownershipEpoch === parent.ownershipEpoch &&
      local.base.snapshotAlarmVersionsByRuntimeKey === parent.snapshotAlarmVersionsByRuntimeKey;
    if (!current) return { ...parent, refresh };
    return {
      ...parent,
      ...(local.data ? mergeWidgetSnapshot(parent, local.data) : {}),
      refresh,
      loadStatus: local.status,
      error: local.error,
      canWrite: parent.canWrite && local.status === 'READY',
    };
  }, [parent, local, refresh]);
  return <IoTFieldDataContext.Provider value={value}>{children}</IoTFieldDataContext.Provider>;
}

/** 확정된 현장 경계에서 V2 projection만 한 번 구독합니다. */
function IoTFieldDataScope({ fieldUid, children }: { fieldUid: string; children: React.ReactNode }) {
  const [state, dispatch] = useReducer(normalizedFieldReducer, fieldUid, createNormalizedFieldState);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [loadState, setLoadState] = useState<{ fieldUid: string; status: IoTFieldDataContextValue['loadStatus']; error: string | null }>({
    fieldUid,
    status: 'LOADING',
    error: null,
  });
  const [connectionState, setConnectionState] = useState<{ fieldUid: string; status: IoTFieldDataContextValue['connectionStatus'] }>({ fieldUid, status: 'IDLE' });
  const refresh = useCallback(() => setRefreshRevision(value => value + 1), []);
  const applyProductSnapshot = useCallback((snapshot: ProductDetailSnapshot) => {
    const entity = snapshot.item;
    if (!isRuntimeEntity(entity) || entity.version < 0 || !entity.runtime_key.startsWith('product:')) return;
    // 응답이 늦어도 현장·운영 세대와 권한 경계는 reducer의 현재 상태로 검사합니다.
    dispatch({
      type: 'PATCH_RECEIVED',
      fieldUid: entity.field_uid,
      routingVersion: snapshot.routing_version,
      ownershipEpoch: snapshot.ownership_epoch,
      eventId: null,
      entity: { ...entity, entity_type: 'PRODUCT' },
    });
  }, []);
  const [rolloutSelection, setRolloutSelection] = useState<{ fieldUid: string; value: FieldRolloutState } | null>(null);
  const rolloutState = rolloutSelection?.fieldUid === fieldUid ? rolloutSelection.value : null;

  useEffect(() => {
    const normalizedFieldUid = fieldUid.trim();
    const abortController = new AbortController();
    let eventSource: EventSource | null = null;
    let disposed = false;
    let rolloutTimer: ReturnType<typeof setInterval> | null = null;
    let checkingRollout = false;
    let streamActive = false;
    let mobileSnapshotNeedsRetry = false;
    let mobileSnapshotInFlight = false;
    let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
    let retryOnReconnect = false;

    /** 자동 재시도를 소진한 일시 오류만 다시 시작하며 권한 차단 상태는 유지합니다. */
    const handleReconnect = () => {
      if (disposed || !retryOnReconnect) return;
      retryOnReconnect = false;
      refresh();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') handleReconnect();
    };
    window.addEventListener('online', handleReconnect);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const closeStream = (status: IoTFieldDataContextValue['connectionStatus'] = 'IDLE') => {
      // 권한 취소·현장 전환 뒤 지연된 open/patch가 기존 상태를 되살리지 못하게 합니다.
      streamActive = false;
      if (heartbeatTimer !== null) clearTimeout(heartbeatTimer);
      heartbeatTimer = null;
      eventSource?.close();
      // 종료된 구독의 권한 확인·초기 조회도 취소해 늦은 응답이 차단 상태를 덮어쓰지 않게 합니다.
      abortController.abort();
      if (rolloutTimer) clearInterval(rolloutTimer);
      rolloutTimer = null;
      if (!disposed) setConnectionState({ fieldUid: normalizedFieldUid, status });
    };
    const loadMobileSnapshot = (rollout: FieldRolloutState) => {
      if (mobileSnapshotInFlight) return;
      mobileSnapshotInFlight = true;
      mobileSnapshotNeedsRetry = false;
      // 모바일의 실패·timeout이 기존 위젯과 SSE 연결을 막지 않도록 기다리지 않습니다.
      void fetchWorkerMobileSnapshot(normalizedFieldUid, abortController.signal, rollout)
        .then(mobileDevices => {
          if (disposed || abortController.signal.aborted) return;
          mobileSnapshotNeedsRetry = false;
          dispatch({
            type: 'MOBILE_SNAPSHOT_RECEIVED',
            fieldUid: normalizedFieldUid,
            routingVersion: rollout.routing_version,
            ownershipEpoch: rollout.ownership_epoch,
            mobileDevices,
          });
        })
        .catch(() => {
          if (disposed || abortController.signal.aborted) return;
          mobileSnapshotNeedsRetry = true;
          dispatch({ type: 'MOBILE_DATA_UNAVAILABLE', fieldUid: normalizedFieldUid, routingVersion: rollout.routing_version, ownershipEpoch: rollout.ownership_epoch });
        })
        .finally(() => {
          mobileSnapshotInFlight = false;
        });
    };

    const load = async () => {
      setLoadState({ fieldUid: normalizedFieldUid, status: 'LOADING', error: null });
      setConnectionState({ fieldUid: normalizedFieldUid, status: 'IDLE' });
      if (!normalizedFieldUid) {
        dispatch({ type: 'FIELD_RESET', fieldUid: normalizedFieldUid, provider: 'V2', routingVersion: 1, ownershipEpoch: 1, reason: 'NO_FIELD' });
        setLoadState({ fieldUid: normalizedFieldUid, status: 'READY', error: null });
        return;
      }

      try {
        const rollout = await retryRead(() => fetchFieldRollout(normalizedFieldUid, abortController.signal), abortController.signal);
        if (disposed || abortController.signal.aborted) return;
        setRolloutSelection({ fieldUid: normalizedFieldUid, value: rollout });
        // V1 현장도 운영 단계와 권한 변경을 확인해 기존 저장소를 계속 조회하지 않게 합니다.
        rolloutTimer = setInterval(async () => {
          if (checkingRollout || disposed || abortController.signal.aborted) return;
          checkingRollout = true;
          try {
            const latest = await fetchFieldRollout(normalizedFieldUid, abortController.signal);
            if (
              !disposed &&
              !abortController.signal.aborted &&
              (latest.active_phase !== rollout.active_phase || latest.routing_version !== rollout.routing_version || latest.ownership_epoch !== rollout.ownership_epoch)
            ) {
              setLoadState({ fieldUid: normalizedFieldUid, status: 'LOADING', error: null });
              closeStream();
              refresh();
            } else if (!disposed && !abortController.signal.aborted && streamActive && mobileSnapshotNeedsRetry) {
              // 기존 현장 확인 주기에서 실패한 모바일 조회만 다시 시도합니다.
              loadMobileSnapshot(latest);
            }
          } catch (error) {
            if (!disposed && !abortController.signal.aborted) {
              const status = isRecord(error) ? error.status : null;
              if (status === 401 || status === 403) {
                closeStream('FAILED');
                setLoadState({
                  fieldUid: normalizedFieldUid,
                  status: status === 403 ? 'FORBIDDEN' : 'ERROR',
                  error: error instanceof Error ? error.message : '현장 운영 정보를 확인하지 못했습니다.',
                });
              } else {
                // 일시적인 확인 실패는 다음 주기에 재시도하고 이미 조회한 위젯을 유지합니다.
                setConnectionState({ fieldUid: normalizedFieldUid, status: 'RECONNECTING' });
              }
            }
          } finally {
            checkingRollout = false;
          }
        }, 30_000);
        const provider = selectFieldDataProvider(rollout.active_phase);
        dispatch({
          type: 'FIELD_RESET',
          fieldUid: normalizedFieldUid,
          provider,
          routingVersion: rollout.routing_version,
          ownershipEpoch: rollout.ownership_epoch,
          reason: 'PROVIDER_SELECTED',
        });

        // 조회 시작 이전의 커서부터 재생해야 초기 조회와 구독 사이의 관측도 누락되지 않습니다.
        const cursor = await retryRead(() => fetchFieldStreamCursor(normalizedFieldUid, abortController.signal), abortController.signal);
        const snapshot = await retryRead(async () => {
          const attempt = new AbortController();
          try {
            return await fetchFieldProjectionSnapshot(normalizedFieldUid, AbortSignal.any([abortController.signal, attempt.signal]), rollout);
          } finally {
            // 병렬 초기 조회 중 하나가 실패하면 나머지도 취소해 다음 재시도와 중첩되지 않게 합니다.
            attempt.abort();
          }
        }, abortController.signal);
        if (disposed || abortController.signal.aborted) return;
        dispatch({
          type: 'SNAPSHOT_RECEIVED',
          fieldUid: normalizedFieldUid,
          provider,
          routingVersion: rollout.routing_version,
          ownershipEpoch: rollout.ownership_epoch,
          ...snapshot,
        });

        setLoadState({ fieldUid: normalizedFieldUid, status: 'READY', error: null });
        loadMobileSnapshot(rollout);
        let streamCursor = cursor;
        const connectStream = () => {
          const source = new EventSource(`/api/v2/fields/${encodeURIComponent(normalizedFieldUid)}/stream?preview=true&after=${encodeURIComponent(streamCursor)}`, {
            withCredentials: true,
          });
          eventSource = source;
          streamActive = true;
          setConnectionState({ fieldUid: normalizedFieldUid, status: 'CONNECTING' });
          const watchActivity = () => {
            if (heartbeatTimer !== null) clearTimeout(heartbeatTimer);
            heartbeatTimer = setTimeout(() => {
              if (disposed || !streamActive || eventSource !== source) return;
              setConnectionState({ fieldUid: normalizedFieldUid, status: 'RECONNECTING' });
              source.close();
              connectStream();
            }, 30_000);
          };
          const noteActivity = () => {
            setConnectionState({ fieldUid: normalizedFieldUid, status: 'CONNECTED' });
            watchActivity();
          };
          watchActivity();
          source.addEventListener('heartbeat', event => {
            if (disposed || !streamActive || eventSource !== source) return;
            try {
              const payload: unknown = JSON.parse((event as MessageEvent<string>).data);
              if (!isRecord(payload) || payload.field_uid !== normalizedFieldUid || typeof payload.server_time !== 'string' || !Number.isFinite(Date.parse(payload.server_time)))
                return;
              noteActivity();
            } catch {
              // 형식이 잘못된 heartbeat는 정상 활동으로 간주하지 않습니다.
            }
          });
          source.addEventListener('error', () => {
            if (disposed || !streamActive || eventSource !== source) return;
            // 브라우저가 자동 재연결을 중단해도 무응답 감시에서 재구독하며 위젯은 유지합니다.
            setConnectionState({ fieldUid: normalizedFieldUid, status: 'RECONNECTING' });
          });
          source.addEventListener('open', () => {
            if (disposed || !streamActive || eventSource !== source) return;
            setConnectionState({ fieldUid: normalizedFieldUid, status: 'CONNECTED' });
          });
          source.addEventListener('patch', event => {
            if (disposed || !streamActive || eventSource !== source) return;
            const message = event as MessageEvent<string>;
            try {
              const payload = JSON.parse(message.data) as unknown;
              if (isRecord(payload) && payload.entity_type === 'MOBILE_SAFETY_EVENT' && !workerSafetyEventSchema.safeParse(payload).success) {
                noteActivity();
                streamCursor = message.lastEventId || streamCursor;
                return;
              }
              if (isRecord(payload) && payload.entity_type === 'MOBILE_DEVICE') {
                if (payload.field_uid !== normalizedFieldUid) return;
                const mobile = workerMobileProjectionSchema.safeParse(payload);
                if (!mobile.success || mobile.data.routing_version !== rollout.routing_version || mobile.data.ownership_epoch !== rollout.ownership_epoch) {
                  mobileSnapshotNeedsRetry = true;
                  noteActivity();
                  streamCursor = message.lastEventId || streamCursor;
                  dispatch({
                    type: 'MOBILE_DATA_UNAVAILABLE',
                    fieldUid: normalizedFieldUid,
                    routingVersion: rollout.routing_version,
                    ownershipEpoch: rollout.ownership_epoch,
                    eventId: message.lastEventId || null,
                  });
                  return;
                }
              }
              if (!isRuntimeEntity(payload)) throw new Error('실시간 응답 형식이 올바르지 않습니다. 다시 조회해 주세요.');
              if (payload.field_uid !== normalizedFieldUid) return;
              if (Number(payload.routing_version) !== rollout.routing_version || Number(payload.ownership_epoch) !== rollout.ownership_epoch) {
                setLoadState({ fieldUid: normalizedFieldUid, status: 'LOADING', error: null });
                closeStream();
                refresh();
                return;
              }
              noteActivity();
              streamCursor = message.lastEventId || streamCursor;
              dispatch({
                type: 'PATCH_RECEIVED',
                fieldUid: normalizedFieldUid,
                routingVersion: Number(payload.routing_version),
                ownershipEpoch: Number(payload.ownership_epoch),
                eventId: message.lastEventId || null,
                entity: payload,
              });
            } catch (error) {
              closeStream('FAILED');
              setLoadState({ fieldUid: normalizedFieldUid, status: 'ERROR', error: error instanceof Error ? error.message : '실시간 응답을 확인하지 못했습니다.' });
            }
          });
          source.addEventListener('FIELD_RESET', event => {
            if (disposed || !streamActive || eventSource !== source) return;
            const message = event as MessageEvent<string>;
            const payload = (() => {
              try {
                return JSON.parse(message.data) as unknown;
              } catch {
                return null;
              }
            })();
            const reason = isRecord(payload) && typeof payload.reason === 'string' ? payload.reason : 'STREAM_RESET';
            if (reason === 'CONFIGURATION_CHANGED') {
              // 모델·장비 허용 변경은 다른 화면의 방송 대상에도 즉시 반영합니다.
              notifyBroadcastConfigurationChanged();
            }
            if (reason === 'PERMISSION_REVOKED') {
              dispatch({ type: 'FIELD_ACCESS_REVOKED', fieldUid: normalizedFieldUid, reason });
              setLoadState({ fieldUid: normalizedFieldUid, status: 'FORBIDDEN', error: '이 현장에 접근할 권한이 없습니다.' });
              closeStream('FAILED');
              return;
            }
            closeStream();
            eventSource = null;
            setLoadState({ fieldUid: normalizedFieldUid, status: 'LOADING', error: null });
            // 운영 버전 변경은 서버에서 다시 확인하며 저장된 선택값만 바꾸는 것으로 끝내지 않습니다.
            refresh();
          });
        };
        connectStream();
      } catch (error) {
        if (disposed || abortController.signal.aborted) return;
        retryOnReconnect = isRetryableReadError(error);
        closeStream('FAILED');
        const status = isRecord(error) && typeof error.status === 'number' ? error.status : null;
        // 실패를 빈 데이터나 V1 응답으로 바꾸지 않아 잘못된 조회와 저장을 막습니다.
        setLoadState({
          fieldUid: normalizedFieldUid,
          status: status === 403 ? 'FORBIDDEN' : 'ERROR',
          error: error instanceof Error ? error.message : '현장 데이터를 불러오지 못했습니다.',
        });
      }
    };

    void load();
    return () => {
      disposed = true;
      window.removeEventListener('online', handleReconnect);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      abortController.abort();
      closeStream();
    };
  }, [fieldUid, refreshRevision, refresh]);

  const contextValue = useMemo<IoTFieldDataContextValue>(
    () => ({
      ...state,
      activePhase: rolloutState?.active_phase ?? 'V2_ACTIVE',
      refresh,
      applyProductSnapshot,
      loadStatus: loadState.fieldUid === fieldUid ? loadState.status : 'LOADING',
      connectionStatus: connectionState.fieldUid === fieldUid ? connectionState.status : 'IDLE',
      error: loadState.fieldUid === fieldUid ? loadState.error : null,
      // 최신 연결 상태가 확인되지 않은 동안 장비 변경·제어를 허용하지 않습니다.
      canWrite:
        loadState.fieldUid === fieldUid &&
        loadState.status === 'READY' &&
        rolloutState?.active_phase === 'V2_ACTIVE' &&
        connectionState.fieldUid === fieldUid &&
        connectionState.status === 'CONNECTED',
    }),
    [rolloutState, state, refresh, applyProductSnapshot, loadState, connectionState, fieldUid]
  );
  return (
    <IoTFieldDataContext.Provider value={contextValue}>
      <DeviceWarningClearStateProvider>{children}</DeviceWarningClearStateProvider>
    </IoTFieldDataContext.Provider>
  );
}

export function useIoTFieldData(): IoTFieldDataContextValue {
  return useContext(IoTFieldDataContext);
}
